import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { MdOpenInNew, MdRefresh } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';
import config from '../../config';
import { useAppInfo } from '../../helpers/hooks/common-hook';
import { getDefaultAccessKeys } from '../../services/app/app-access-key-service';
import { getConfig } from '../../services/config';

const buttonClass = 'rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

// The debugger belongs to the platform's light workspace. Supply its palette
// explicitly so an unconfigured preview doesn't inherit the public widget's
// dark defaults. Saved integration colors still take precedence.
const previewColors = {
    primaryColor: '#bae61a',
    headerBackgroundColor: '#ffffff',
    headerTextColor: '#171717',
    chatWindowBackgroundColor: '#ffffff',
    sendBarBackgroundColor: '#f3f4f6',
};

// A new app/session gets a fresh frame and credentials; late responses from the
// previous app must never be delivered into the new app's iframe.
export default function ChatPreview(props) {
    const { appId } = useAppInfo();
    return <ChatPreviewSession key={`${appId}:${props.sessionId}`} appId={appId} {...props} />;
}

function ChatPreviewSession({ appId, sessionId, currentJourneySession, expanded = false, actionsContainer }) {
    const navigate = useNavigate();
    const frame = useRef(null);
    const external = useRef(null);
    const widgetReady = useRef(false);
    const [bootstrap, setBootstrap] = useState(null);
    const [error, setError] = useState('');
    const [connected, setConnected] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const src = appId && sessionId && config.WEB_PLUGIN_URL
        ? `${config.WEB_PLUGIN_URL.replace(/\/$/, '')}/?${new URLSearchParams({ app_id: appId, test_mode: '1', session_id: sessionId, test_voice_mode: '1', preview: '1' })}`
        : '';
    const origin = src ? new URL(src, window.location.href).origin : '';
    const sendBootstrap = useCallback(target => {
        if (bootstrap && target && origin) target.postMessage({ type: 'botcircuits_preview_settings', ...bootstrap }, origin);
    }, [bootstrap, origin]);

    useEffect(() => {
        if (!appId || !sessionId) return undefined;
        let cancelled = false;
        Promise.all([getDefaultAccessKeys(appId), getConfig(`app_${appId}`, 'web_integration')])
            .then(([keys, settings]) => {
                if (cancelled) return;
                if (!keys?.accessKey) throw new Error('Unable to authorize Chat Preview. Please retry.');
                const savedSettings = settings?.data || {};
                setBootstrap({ accessKey: keys.accessKey, settings: {
                    ...previewColors,
                    ...savedSettings,
                    // Older integrations use the header color as their brand.
                    primaryColor: savedSettings.primaryColor || savedSettings.headerBackgroundColor || previewColors.primaryColor,
                } });
            })
            .catch(() => { if (!cancelled) setError('Unable to load Chat Preview. Please retry.'); });
        return () => { cancelled = true; };
    }, [appId, sessionId, attempt]);

    useEffect(() => {
        if (!src) return undefined;
        const receive = event => {
            if (event.origin !== origin || event.data?.type !== 'botcircuits_preview_ready') return;
            if (event.source === frame.current?.contentWindow) {
                widgetReady.current = true;
                sendBootstrap(event.source);
                if (bootstrap) { setConnected(true); setError(''); }
            } else if (external.current && event.source === external.current) {
                sendBootstrap(event.source);
            }
        };
        window.addEventListener('message', receive);
        // The widget announces readiness after installing its message listener.
        // Credentials may finish loading before or after that announcement.
        if (widgetReady.current && bootstrap) {
            sendBootstrap(frame.current?.contentWindow);
            setConnected(true);
            setError('');
        }
        return () => window.removeEventListener('message', receive);
    }, [src, origin, bootstrap, sendBootstrap]);

    useEffect(() => {
        if (!src || connected || error) return undefined;
        const timeout = window.setTimeout(() => setError('Chat Preview did not load. Check your connection and retry.'), 15000);
        return () => window.clearTimeout(timeout);
    }, [src, connected, error, attempt]);

    const retry = () => {
        widgetReady.current = false;
        setBootstrap(null);
        setConnected(false);
        setError('');
        setAttempt(value => value + 1);
    };
    const openExternal = () => {
        const url = new URL(src, window.location.href);
        url.searchParams.delete('session_id');
        // Hand credentials to this specific window when it announces readiness;
        // never put access keys into URLs, browser history or referrers.
        external.current = window.open(url.href, '_blank');
    };

    return (
        <div className={`flex h-full ${expanded ? 'min-w-0 flex-1' : 'w-[320px] shrink-0 2xl:w-96'} flex-col border-l border-gray-200 bg-white`}>
            {actionsContainer && createPortal(
                <div className="flex shrink-0 items-center gap-1">
                    <button type="button" aria-label="Open preview in a new tab" title="Open in new tab" disabled={!bootstrap || !src} className={`${buttonClass} disabled:opacity-40`} onClick={openExternal}><MdOpenInNew size={20} /></button>
                    <button type="button" aria-label="Close debugger" title="Exit Simulator" className={buttonClass} onClick={() => {
                        if (currentJourneySession?.runningBlock) {
                            localStorage.setItem('lastWorkflowStep', currentJourneySession.runningBlock);
                            localStorage.setItem('lastJourneyId', currentJourneySession.journeyId);
                        }
                        navigate(`/app/${appId}/console`);
                    }}><IoMdClose size={20} /></button>
                </div>,
                actionsContainer
            )}
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                {src && <iframe key={attempt} ref={frame} title="webchat plugin" src={src} className="min-h-0 w-full flex-1 border-0" allow="microphone" onLoad={() => sendBootstrap(frame.current?.contentWindow)} onError={() => setError('Chat Preview did not load. Please retry.')} />}
                {(!connected || error) && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white p-5 text-center text-sm text-gray-500">
                    <p role={error || !config.WEB_PLUGIN_URL ? 'alert' : 'status'}>{!config.WEB_PLUGIN_URL ? 'Chat Preview is not configured for this environment.' : error || 'Loading Chat Preview…'}</p>
                    {error && <button type="button" onClick={retry} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 font-medium hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500"><MdRefresh size={18} />Retry preview</button>}
                </div>}
            </div>
        </div>
    );
}
